import React from "react";
import { motion } from "framer-motion";
import { 
  Database, 
  TerminalWindow, 
  Cpu, 
  Users, 
  ChatCircle,
  Trophy,
  GraduationCap,
  Briefcase,
  RocketLaunch,
  CheckCircle,
  Star
} from "@phosphor-icons/react";
import { cn } from "@stylingcv/utils";

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-10 text-red-500">
          <h1>Something went wrong.</h1>
          <pre>{this.state.error?.message}</pre>
          <pre>{this.state.error?.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const ResumeSectionsShowcaseContent = () => {
  return (
    <div className="relative flex h-[600px] w-full items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-white p-10 dark:from-gray-900 dark:to-gray-950">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]" />

      <div className="relative h-full w-full max-w-4xl">
        {/* Hand drawn arrow (SVG) - pointing to main card */}
        <motion.div
            initial={{ opacity: 0, pathLength: 0 }}
            animate={{ opacity: 1, pathLength: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="absolute left-[15%] top-[10%] z-10 hidden md:block"
        >
             <svg width="150" height="100" viewBox="0 0 150 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <motion.path 
                    d="M10 10 C 50 10, 80 50, 120 80" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    className="text-gray-400 dark:text-gray-600"
                    strokeDasharray="5 5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                />
                <motion.path 
                    d="M110 70 L 120 80 L 100 90" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    className="text-gray-400 dark:text-gray-600"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 0.3 }}
                />
            </svg>
        </motion.div>

        {/* 1. Main Focus Card: TECHNICAL SKILLS */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute left-[10%] top-[25%] z-30 w-[380px] rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl shadow-blue-900/10 dark:border-gray-800 dark:bg-gray-900"
        >
          {/* Recommended Badge */}
          <div className="absolute -left-3 -top-3 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 px-4 py-1.5 text-xs font-bold text-white shadow-lg shadow-emerald-500/20">
            RECOMMENDED
          </div>

          <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white border-b-2 border-gray-900 pb-2 dark:border-white">
            TECHNICAL SKILLS
          </h3>

          <div className="space-y-6">
            <div>
              <h4 className="mb-3 text-sm font-semibold text-blue-600 dark:text-blue-400">Data</h4>
              <div className="flex flex-wrap gap-2">
                {["Databases", "Linux", "Unix", "ETL"].map((skill) => (
                  <span key={skill} className="rounded-md bg-gray-100 px-2.5 py-1 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="mb-3 text-sm font-semibold text-blue-600 dark:text-blue-400">Management</h4>
              <div className="flex flex-wrap gap-2">
                {["CSM", "Team Work", "Communication"].map((skill) => (
                  <span key={skill} className="rounded-md bg-gray-100 px-2.5 py-1 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* 2. Education Card (Right, Behind) */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="absolute right-[10%] top-[20%] z-20 w-[320px] rounded-xl border border-gray-100 bg-white/90 p-5 shadow-xl backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/90"
        >
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white border-b-2 border-black pb-1 dark:border-white">
            EDUCATION
          </h3>
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Landmark University</div>
              <div className="text-sm text-blue-600 dark:text-blue-400">Team Management and Leadership Program</div>
              <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                <CheckCircle size={12} weight="fill" /> 2017 - 2019
              </div>
            </div>
            <div className="opacity-60">
                <div className="font-semibold text-gray-900 dark:text-white">DeVry University</div>
                <div className="text-sm text-blue-600 dark:text-blue-400">Bachelor's of Management</div>
            </div>
          </div>
        </motion.div>

        {/* 3. Experience Card (Bottom Right, Behind) */}
        <motion.div
           initial={{ opacity: 0, y: 50 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.5, delay: 0.4 }}
           className="absolute bottom-[10%] right-[5%] z-20 w-[340px] rounded-xl border border-gray-100 bg-white/95 p-5 shadow-xl dark:border-gray-800 dark:bg-gray-900/95"
        >
           <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white border-b-2 border-black pb-1 dark:border-white">
            EXPERIENCE
          </h3>
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Solutions Architect & Technical Manager</div>
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Acceleron Industries</div>
              <div className="mt-1 text-xs text-gray-500">2019 - Showing</div>
              <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                Launched and deployed technology solutions, providing technical and functional support for 10-20 clients...
              </p>
            </div>
          </div>
        </motion.div>

        {/* 4. Awards Card (Top Left, Behind) */}
        <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="absolute left-[5%] top-[5%] z-10 w-[260px] rounded-xl border border-gray-100 bg-white/80 p-4 shadow-lg blur-[1px] dark:border-gray-800 dark:bg-gray-900/80"
        >
             <h3 className="mb-2 text-base font-bold text-gray-900 dark:text-white border-b border-black pb-1 dark:border-white">
                AWARDS
            </h3>
            <div className="flex items-start gap-2">
                <Trophy size={16} className="mt-1 text-yellow-500" weight="fill"/>
                <div>
                     <div className="text-sm font-semibold">Design Award</div>
                     <div className="text-xs text-gray-500">Company Name</div>
                </div>
            </div>
        </motion.div>

        {/* 5. Projects (Most Proud Of) Card (Middle Top, Behind) */}
        <motion.div
             initial={{ opacity: 0, y: -30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.5, delay: 0.35 }}
             className="absolute left-[40%] top-[8%] z-10 w-[280px] rounded-xl border border-gray-100 bg-white/80 p-4 shadow-lg blur-[0.5px] dark:border-gray-800 dark:bg-gray-900/80"
        >
            <h3 className="mb-2 text-base font-bold text-gray-900 dark:text-white border-b border-black pb-1 dark:border-white">
                MOST PROUD OF
            </h3>
             <div className="flex items-start gap-2">
                <RocketLaunch size={16} className="mt-1 text-purple-500" weight="fill"/>
                <div>
                     <div className="text-sm font-semibold">Scalable Microservices</div>
                     <div className="text-xs text-gray-500">Developed high-performance services</div>
                </div>
            </div>
        </motion.div>

      </div>
    </div>
  );
};

export const ResumeSectionsShowcase = () => (
    <ErrorBoundary>
        <ResumeSectionsShowcaseContent />
    </ErrorBoundary>
);
